import { useState, type FormEvent } from "react"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowLeft, ArrowRight, KeyRound, Mail } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { AuthLayout } from "@/components/auth/auth-layout"
import { Field } from "@/components/workspace/primitives"
import { authClient } from "@/lib/auth-client"

export const Route = createFileRoute("/forgot-password")({ component: ForgotPassword })

/** Screen 50. The confirmation never reveals whether the address has an account. */
function ForgotPassword() {
  const [email, setEmail] = useState("")
  const [sent, setSent] = useState(false)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    // Absolute so the link works when the API and web app run on different origins (local dev).
    await authClient.requestPasswordReset({ email, redirectTo: `${window.location.origin}/reset-password` })
    setBusy(false)
    setSent(true)
  }

  return (
    <AuthLayout
      mark={<KeyRound size={23} />}
      title={sent ? "Check your inbox" : "Forgot your password?"}
      subtitle={sent ? `If an account exists for ${email}, you’ll receive a password reset link.` : "Enter your email and we’ll send you a reset link."}
    >
      {sent ? (
        <div className="ws-auth-confirmation">
          <div className="ws-confirm-icon">
            <Mail size={26} />
          </div>
          <p>Check your spam folder if you don’t see the email. Reset links can be used only once.</p>
          <Button
            className="w-full"
            variant="outline"
            onClick={() => {
              setSent(false)
              toast("You can request another reset link")
            }}
          >
            Use a different email
          </Button>
          <Button variant="ghost" className="w-full" asChild>
            <Link to="/sign-in">
              <ArrowLeft />
              Back to sign in
            </Link>
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} className="ws-auth-fields">
          <Field label="Work email">{(id) => <Input id={id} type="email" value={email} required autoComplete="email" autoFocus onChange={(e) => setEmail(e.target.value)} />}</Field>
          <Button className="w-full" size="lg" type="submit" disabled={busy}>
            Send reset link
            <ArrowRight />
          </Button>
          <Button className="w-full" variant="ghost" type="button" asChild>
            <Link to="/sign-in">
              <ArrowLeft />
              Back to sign in
            </Link>
          </Button>
        </form>
      )}
    </AuthLayout>
  )
}
